import { formatDateForAPI } from "@/utils/dateUtils";
// GL Account defaults
export const GL_ACCOUNT_DEFAULTS = {
  type: 2, // LIABILITY
  usage: 1, // DETAIL
  manualEntriesAllowed: true,
};

export function getDefaultGlAccountPayload(overrides = {}) {
  return {
    type: GL_ACCOUNT_DEFAULTS.type,
    usage: GL_ACCOUNT_DEFAULTS.usage,
    manualEntriesAllowed: GL_ACCOUNT_DEFAULTS.manualEntriesAllowed,
    parentId: null,
    description: "",
    ...overrides,
  };
}

// Form defaults
export const FORM_DEFAULTS = {
  digitsAfterDecimal: 2,
  accountingRule: 1,
  advancedAccountingRules: true,
};

// Dropdown options
export const DROPDOWN_OPTIONS = {
  lockinPeriodFrequencyTypeOptions: [
    { id: 1, name: "Days" },
    { id: 2, name: "Weeks" },
    { id: 3, name: "Months" },
    { id: 4, name: "Years" },
  ],
  accountingMethodOptions: [
    { id: 1, name: "None" },
    { id: 2, name: "Cash" },
    { id: 3, name: "Accrual (periodic)" },
  ],
};

// Accounting mappings structure (for form display only)
export const ACCOUNTING_MAPPINGS_STRUCTURE = {
  savingsControlAccountId: null,
  savingsReferenceAccountId: null,
  transfersInSuspenseAccountId: null,
  interestOnSavingsAccountId: null,
  incomeFromFeeAccountId: null,
  incomeFromPenaltyAccountId: null,
  overdraftPortfolioControlId: null,
  incomeFromInterestId: null,
  writeOffAccountId: null,
  feesReceivableAccountId: null,
  penaltiesReceivableAccountId: null,
  interestPayableAccountId: null,
};

// Program defaults
export const PROGRAM_DEFAULTS = {
  locale: "en",
  nominalAnnualInterestRate: 0,
  interestCompoundingPeriodType: 1,
  interestPostingPeriodType: 4,
  interestCalculationType: 1,
  interestCalculationDaysInYearType: 365,
  minRequiredOpeningBalance: 0,
  enforceMinRequiredBalance: false,
  minBalanceForInterestCalculation: 0,
  withdrawalFeeForTransfers: false,
  withdrawalFeeAmount: 0,
  allowOverdraft: false,
  withHoldTax: false,
  isDormancyTrackingActive: false,
};

// Helper to build program payload from form data
export function buildProgramPayload(
  formData: Record<string, unknown>,
): Record<string, unknown> {
  const mappings =
    (formData.accountingMappings as Record<string, unknown>) || {};
  const toId = (v: unknown): unknown =>
    typeof v === "object"
      ? ((v as Record<string, unknown>)?.id ??
        (v as Record<string, unknown>)?.value ??
        null)
      : (v ?? null);
  const normalizedCharges = Array.isArray(formData.charges)
    ? (formData.charges as Array<unknown>)
        .map((charge: unknown) => toId(charge))
        .filter((id: unknown) => id != null)
        .map((id: unknown) => ({ id }))
    : [];
  return {
    // ...PROGRAM_DEFAULTS,
    name:
      typeof formData.name === "string" ? formData.name.trim() : formData.name,
    shortName:
      typeof formData.shortName === "string"
        ? formData.shortName.trim()
        : formData.shortName,
    description:
      typeof formData.description === "string"
        ? formData.description.trim() || ""
        : "",
    // Ensure currencyCode is always a string code like "USD"
    currencyCode:
      typeof formData.currency === "string"
        ? formData.currency
        : (formData.currency as Record<string, unknown> | undefined)?.code ||
          (formData.currency as Record<string, unknown> | undefined)?.value ||
          (formData.currency as Record<string, unknown> | undefined)?.name ||
          null,
    digitsAfterDecimal:
      formData.digitsAfterDecimal || FORM_DEFAULTS.digitsAfterDecimal,
    inMultiplesOf: formData.inMultiplesOf || 1,
    accountingRule: formData.accountingRule || 1,
    charges: normalizedCharges,
    // Accounting mappings (only primitive ids)
    savingsControlAccountId: toId(mappings.savingsControlAccountId),
    savingsReferenceAccountId: toId(mappings.savingsReferenceAccountId),
    transfersInSuspenseAccountId: toId(mappings.transfersInSuspenseAccountId),
    interestOnSavingsAccountId: toId(mappings.interestOnSavingsAccountId),
    incomeFromFeeAccountId: toId(mappings.incomeFromFeeAccountId),
    incomeFromPenaltyAccountId: toId(mappings.incomeFromPenaltyAccountId),
    overdraftPortfolioControlId: toId(mappings.overdraftPortfolioControlId),
    incomeFromInterestId: toId(mappings.incomeFromInterestId),
    writeOffAccountId: toId(mappings.writeOffAccountId),
    feesReceivableAccountId: toId(mappings.feesReceivableAccountId),
    penaltiesReceivableAccountId: toId(mappings.penaltiesReceivableAccountId),
    interestPayableAccountId: toId(mappings.interestPayableAccountId),
    applyWithdrawalFeeForTransfers: formData.applyWithdrawalFeeForTransfers,
    taxGroupId:
      (formData.taxGroupId as Record<string, unknown> | undefined)?.id ||
      formData.taxGroupId ||
      null,
    allowOverdraft: formData.allowOverdraft,
    withHoldTax: formData.withHoldTax,
    withholdTaxGroup: formData.withholdTaxGroup,

    locale: formData.locale || "en",
    nominalAnnualInterestRate: formData.nominalAnnualInterestRate || 0,
    interestCompoundingPeriodType:
      (
        formData.interestCompoundingPeriodType as
          | Record<string, unknown>
          | undefined
      )?.id || 1,
    interestPostingPeriodType:
      (
        formData.interestPostingPeriodType as
          | Record<string, unknown>
          | undefined
      )?.id || 4,
    interestCalculationType:
      (formData.interestCalculationType as Record<string, unknown> | undefined)
        ?.id || 1,
    interestCalculationDaysInYearType:
      (
        formData.interestCalculationDaysInYearType as
          | Record<string, unknown>
          | undefined
      )?.id || 365,
    minRequiredOpeningBalance: formData.minRequiredOpeningBalance || 0,
    enforceMinRequiredBalance: formData.enforceMinRequiredBalance || false,
    minBalanceForInterestCalculation:
      formData.minBalanceForInterestCalculation || null,
    withdrawalFeeForTransfers: formData.withdrawalFeeForTransfers || false,
    withdrawalFeeAmount: formData.withdrawalFeeAmount || null,
    isDormancyTrackingActive: formData.isDormancyTrackingActive || false,
    daysToEscheat: formData.daysToEscheat || null,
    daysToDormancy: formData.daysToDormancy || null,
    daysToInactive: formData.daysToInactive || null,
    minOverdraftForInterestCalculation:
      formData.minOverdraftForInterestCalculation || null,
    nominalAnnualInterestRateOverdraft:
      formData.nominalAnnualInterestRateOverdraft || null,
    overdraftLimit: formData.overdraftLimit || null,
  };
}

// Client defaults
export const CLIENT_DEFAULTS = {
  officeId: 1, // Default to Head Office
  legalForm: "Person",
  isStaff: false,
  active: true,
  addFamilyMember: false,
  familyMemberIsDependent: false,
};

// Generate a random date of birth for an adult (> 18 years old)

// Helper to build client payload from form data
export function buildClientPayload(
  formData: Record<string, unknown>,
): Record<string, unknown> {
  // Default date of birth: 20 years ago from today if not provided
  const defaultDOB = new Date();
  defaultDOB.setFullYear(defaultDOB.getFullYear() - 20);

  const payload: {
    remarks: unknown;
    savingsProductIds: unknown;
    fullname: unknown;
    emailAddress: unknown;
    officeId: unknown;
    mobileNo: unknown;
    dateOfBirth: string;
    externalId: unknown;
    genderId: unknown;
    legalFormId: unknown;
    isStaff: unknown;
    active: unknown;
    submittedOnDate: string;
    activationDate: string;
    dateFormat: string;
    locale: string;
    clientTypeId: unknown;
    clientClassificationId: unknown;
    incorpNumber: unknown;
    constitution: unknown;
    familyMembers: Array<Record<string, unknown>>;
    groupId?: unknown;
  } = {
    remarks: formData.remarks,
    savingsProductIds: formData.savingsProductIds,
    fullname: formData.fullname || "",
    emailAddress: formData.emailAddress,
    officeId: formData.officeId,
    mobileNo: formData.mobileNo,
    dateOfBirth: formatDateForAPI(
      (formData.dateOfBirth as Date | string | number[] | null | undefined) ||
        defaultDOB,
    ),
    externalId: formData.externalId,
    genderId: formData.gender ?? null,
    legalFormId: formData.legalFormId,
    isStaff: formData.isStaff,
    active: formData.active,
    submittedOnDate: formData.submittedOnDate
      ? formatDateForAPI(
          formData.submittedOnDate as
            | Date
            | string
            | number[]
            | null
            | undefined,
        )
      : "",
    activationDate: formData.activationDate
      ? formatDateForAPI(
          formData.activationDate as
            | Date
            | string
            | number[]
            | null
            | undefined,
        )
      : "",
    dateFormat: "dd MMMM yyyy",
    locale: "en",
    clientTypeId: formData.clientType,
    clientClassificationId: formData.clientClassification,
    incorpNumber: formData.incorpNumber,
    constitution: formData.constitution ?? null,

    familyMembers: formData.addFamilyMember
      ? [
          {
            firstname: formData.familyMemberFirstName,
            lastname: formData.familyMemberLastName,
            middlename: formData.familyMemberMiddleName,
            genderId: formData.familyMemberGender,
            relationshipId: formData.familyMemberRelationship,
            isDependent: formData.familyMemberIsDependent,
            professionId: formData.familyMemberProfession,
            maritalStatusId: formData.familyMemberMaritalStatus,
          },
        ]
      : [],
  };

  if (formData.groupId != null && formData.groupId !== "") {
    payload.groupId = formData.groupId;
  }

  return payload;
}

// Build payload for client update (exclude unsupported fields like officeId, programId, familyMembers)
export function buildClientUpdatePayload(
  formData: Record<string, unknown>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    firstname: formData.firstName,
    lastname: formData.lastName,
    fullname: formData.fullname,
    emailAddress: formData.emailAddress,
    mobileNo: formData.mobileNo,
    middlename: formData.middleName,
    externalId: formData.externalId,
    genderId: formData.gender ?? null,
    isStaff: formData.isStaff,
    active: formData.active,
    clientTypeId: formData.clientType,
    clientClassificationId: formData.clientClassification,
    // intentionally omit: officeId, programId, familyMembers, submittedOnDate, activationDate
  };

  // Always include activationDate (required by API)
  payload.activationDate = formatDateForAPI(
    formData.activationDate as Date | string | number[] | null | undefined,
  );
  payload.submittedOnDate =
    formatDateForAPI(
      formData.submittedOnDate as Date | string | number[] | null | undefined,
    ) || null;
  payload.dateFormat = "dd MMMM yyyy";
  payload.locale = "en";

  // Include dateOfBirth only if provided
  if (formData.dateOfBirth) {
    payload.dateOfBirth = formatDateForAPI(
      formData.dateOfBirth as Date | string | number[] | null | undefined,
    );
  }

  return payload;
}

export const PARTICIPANT_TYPES = {
  DONORS: { title: "Donors", icon: "pi pi-heart", id: 4 },
  AGENCIES: { title: "Agencies", icon: "pi pi-sitemap", id: 5 },
  BENEFICIARIES: { title: "Beneficiaries", icon: "pi pi-user-plus", id: 7 },
  PARTNERS: { title: "Partners", icon: "pi pi-link", id: 6 },
  MERCHANTS: { title: "Merchants", icon: "pi pi-shopping-cart", id: 8 },
  BANKS: { title: "Banks", icon: "pi pi-money-bill", id: 9 },
  AUTHORITIES: { title: "Authorities", icon: "pi pi-building", id: 10 },
};

// Participants group mappings used by ParticipantsDetails view
export const PARTICIPANT_GROUP_ID_TO_NAME = {
  5: PARTICIPANT_TYPES.AGENCIES.title,
  6: PARTICIPANT_TYPES.PARTNERS.title,
  7: PARTICIPANT_TYPES.BENEFICIARIES.title,
  8: PARTICIPANT_TYPES.MERCHANTS.title,
  9: PARTICIPANT_TYPES.BANKS.title,
  10: PARTICIPANT_TYPES.AUTHORITIES.title,
  4: PARTICIPANT_TYPES.DONORS.title,
};

// Sidebar long label mappings for compact/tooltip rendering
export const LONG_MENU_LABELS = {
  "Operational Partners": {
    short: "Partners",
    tooltip: "Operational Partners",
  },
  "Financial Institutions": {
    short: "Financial",
    tooltip: "Financial Institutions",
  },
  "Government Bodies": { short: "Government", tooltip: "Government Bodies" },
  Beneficiaries: { short: "Beneficiaries", tooltip: "Beneficiaries" },
  Vendors: { short: "Vendors", tooltip: "Vendors" },
  "E-Commerce": { short: "E-Commerce", tooltip: "E-Commerce Dashboard" },
  Donors: { short: "Donors", tooltip: "Donors" },
};

// Participants categories used across Participants services and views
export const PARTICIPANT_CATEGORIES = [
  PARTICIPANT_TYPES.DONORS.title,
  PARTICIPANT_TYPES.AGENCIES.title,
  PARTICIPANT_TYPES.PARTNERS.title,
  PARTICIPANT_TYPES.BENEFICIARIES.title,
  PARTICIPANT_TYPES.MERCHANTS.title,
  PARTICIPANT_TYPES.BANKS.title,
  PARTICIPANT_TYPES.AUTHORITIES.title,
];

export const CLIENTS_FIELDS = {
  TABLE_NAME: "extra_client_details",
  REPORT_NAME: "oaClientCategoryReport",
  CLIENT_CATEGORY: "OA_CLIENT_CAT",
  CLIENT_COUNTRY: "OA_CLIENT_COUNTRY",
  CLIENT_REMARKS: "OA_CLIENT_REMARKS",
  CLIENT_INCORPORATION_NUMBER: "OA_CLIENT_INCORPORATION_NUMBER",
  DONOR: {
    legalFormId: 2,
    REPORT_NAME: "oaClientDonorReportNew",
    type: "OA_DONOR_TYPE",
    uniqueId: "OA_CLIENT_ID",
    country: "OA_CLIENT_COUNTRY",
    fundingStream: "OA_DONOR_FUNDING_STREAM",
    humanitarianFund: "OA_DONOR_HUMANITARIAN_FUND",
    developmentFund: "OA_DONOR_DEVELOPMENT_FUND",
    stabilizationFund: "OA_DONOR_STABILIZATION_FUND",
    governmentDepartment: "OA_DONOR_GOV_DEP",
    departmentAddress: "OA_DONOR_DEPARTMENT_ADDRESS",
    departmentContactInfo: "OA_DONOR_DEPARTMENT_CONTACT_INFO",
    savings_account_ids: "OA_CLIENT_SAVINGS_ACCOUNT_IDS",
  },
  AGENCIES: {
    legalFormId: 2,
    REPORT_NAME: "oaClientAgencyReportNew",
    type: "OA_AGENCY_TYPE",
    level: "OA_AGENCY_LEVEL",
    headquarterAddress: "OA_AGENCY_HEADQUARTER_ADDRESS",
    country: "OA_CLIENT_COUNTRY",
    remarks: "OA_CLIENT_REMARKS",
    registrationNumber: "OA_AGENCY_REG_NUMBER",
    savings_account_ids: "OA_CLIENT_SAVINGS_ACCOUNT_IDS",
  },
  BENEFICIARIES: {
    legalFormId: 1,
    REPORT_NAME: "oaClientBeneficiaryReportNew",
    ban: "OA_BENEFICIARY_BAN",
    nik: "OA_BENEFICIARY_NIK",
    savings_account_ids: "OA_CLIENT_SAVINGS_ACCOUNT_IDS",
  },
  PARTNERS: {
    legalFormId: 2,
    REPORT_NAME: "oaClientPartnerReportNew",
    uniqueId: "OA_CLIENT_ID",
    businessLines: "OA_CLIENT_BUSINESS_LINES",
    erpId: "OA_CLIENT_OMNIAID_ERP_ID",
    remarks: "OA_CLIENT_REMARKS",
    incorporationNumber: "OA_CLIENT_INCORPORATION_NUMBER",
    incorporationValidityTill: "OA_CLIENT_INCORPORATION_VALIDITY_TILL_DATE",
    incorporationDate: "OA_CLIENT_INCORPORATION_DATE",
    partnerId: "OA_PARTNER_ID",
    country: "OA_CLIENT_COUNTRY",
    savings_account_ids: "OA_CLIENT_SAVINGS_ACCOUNT_IDS",
  },
  MERCHANTS: {
    legalFormId: 2,
    REPORT_NAME: "oaClientMerchantsReportNew",
    uniqueId: "OA_CLIENT_ID",
    erpId: "OA_CLIENT_OMNIAID_ERP_ID",
    businessLines: "OA_CLIENT_BUSINESS_LINES",
    remarks: "OA_CLIENT_REMARKS",
    incorporationNumber: "OA_CLIENT_INCORPORATION_NUMBER",
    incorporationValidityTill: "OA_CLIENT_INCORPORATION_VALIDITY_TILL_DATE",
    incorporationDate: "OA_CLIENT_INCORPORATION_DATE",
    savings_account_ids: "OA_CLIENT_SAVINGS_ACCOUNT_IDS",
  },
  BANKS: {
    legalFormId: 2,
    REPORT_NAME: "oaClientBanksReportNew",
    uniqueId: "OA_CLIENT_ID",
    erpId: "OA_CLIENT_OMNIAID_ERP_ID",
    remarks: "OA_CLIENT_REMARKS",
    incorporationNumber: "OA_CLIENT_INCORPORATION_NUMBER",
    incorporationValidityTill: "OA_CLIENT_INCORPORATION_VALIDITY_TILL_DATE",
    incorporationDate: "OA_CLIENT_INCORPORATION_DATE",
  },
  AUTHORITIES: {
    legalFormId: 2,
    REPORT_NAME: "oaClientAuthoritiesReportNew",
    uniqueId: "OA_CLIENT_ID",
    erpId: "OA_CLIENT_OMNIAID_ERP_ID",
    constitution: "OA_CLIENT_CONSTITUTION",
    remarks: "OA_CLIENT_REMARKS",
    incorporationNumber: "OA_CLIENT_INCORPORATION_NUMBER",
    incorporationValidityTill: "OA_CLIENT_INCORPORATION_VALIDITY_TILL_DATE",
    incorporationDate: "OA_CLIENT_INCORPORATION_DATE",
  },
};

export const PRODUCT_DEFAULTS = {
  TABLE_NAME: "extra_project_details",
  REPORT_HPROX_YES: "HPROXYES1",
  REPORT_HPROX_NO: "HPROXNO",
  REPORT_HPROX_ALL: "HPROXALL",
  hprox: "OA_HPROX",
  projectNarrative: "OA_PROJECT_NARRATIVE",
  projectSpecific: "OA_PROJECT_SPECIFIC",
  projectPrincipal: "OA_PROJECT_PRINCIPAL",
  projectTransfer: "OA_PROJECT_TRANSFER",
  humanitarianOrgName: "OA_PROJECT_HUM_ORG_NAME",
};

export const SAVINGS_ACCOUNT_DETAILS = {
  TABLE_NAME: "extra_savings_details",
  REPORT_NAME: "oaSavingsAccountDetailsReport",
  CARD_ID: "OA_CARD_ID",
  MPESA_ID: "OA_MPESA_ID",
  MOMO_ID: "OA_MOMO_ID",
  POS_ID: "OA_POS_ID",
  AIRTEL_MONEY_ID: "OA_AIRTEL_MONEY_ID",
  VODA_PAY_ID: "OA_VODA_PAY_ID",
};

export function buildCustomFieldPayload(
  formData: Record<string, unknown>,
): Record<string, unknown> {
  return {
    name: formData.name,
    description: formData.description,
    type: formData.type,
    options: formData.options,
  };
}
